<template>
  <div>
    <div class="card">
      <div class="card-body">
        <sweet-button :loading="isLoading" variant="primary" icon="bx bx-plus" size="sm" class="float-right" @click="openCreateModal()">
          {{ $t("workspace.create-a-new-workspace") }}
        </sweet-button>

        <right-panel class="float-right" id="sidebar-members-edit" width="800px" @hidden="rightPanelClosed">
          <div class="container">
            <b-tabs justified nav-class="nav-tabs-custom" content-class="p-3 text-muted">
              <b-tab>
                <template v-slot:title>
                  <span class="d-inline-block d-sm-none">
                    <i class="fas fa-home"></i>
                  </span>
                  <span class="d-none d-sm-inline-block">{{ $t("workspace.members") }}</span>
                </template>
                <management-workspace-members-view v-if="selectedEntity" :workspace="selectedEntity" />
              </b-tab>

              <b-tab>
                <template v-slot:title>
                  <span class="d-inline-block d-sm-none">
                    <i class="far fa-user"></i>
                  </span>
                  <span class="d-none d-sm-inline-block">
                    <span>{{ $t("workspace.invite-text") }} </span>
                  </span>
                </template>
                <div>
                  <h6>{{ $t("workspace.invite-text") }}</h6>
                  <small>{{ $t("workspace.can-invite") }}</small>
                </div>

                <WorkspaceInviteMember
                  v-if="selectedEntity"
                  :workspace="selectedEntity"
                  :owner="selectedEntity.owner"
                  @onSent="OnInvited()"
                  scope="MANAGEMENT"
                  ref="workspaceInviteMember"
                  @closeModal="closeEditMember()"
                />
              </b-tab>
            </b-tabs>
          </div>
        </right-panel>
        <right-panel class="float-right" id="sidebar-edit-workspace-purchased-feature" width="1000px" @hidden="rightPanelClosed">
          <div class="pl-5 pr-5">
            <h2>{{ $t("purchased-feature.edit") }}</h2>

            <b-card v-if="editedEntity">
              <h4>{{ editedEntity.name }}</h4>

              <b-form-checkbox switch size="sm" v-model="purchasedFeatures.hasOrganizationLimit" :value="true" :unchecked-value="false">
                {{ $t("purchased-feature.organization-limit") }}
              </b-form-checkbox>

              <sweet-input
                v-if="purchasedFeatures.hasOrganizationLimit"
                v-model="purchasedFeatures.organizationLimit"
                :isValid="!$v.purchasedFeatures.organizationLimit.$error"
                :placeholder="$t('purchased-feature.organization-limit')"
                @input="$v.purchasedFeatures.organizationLimit.$touch()"
                type="number"
                layout="vertical"
                inputClass="mb-1"
              />

              <sweet-button @click="savePurchasedFeatures" type="submit" :disabled="$v.$invalid" variant="success" icon="mdi mdi-check" size="sm" buttonClass="mt-4">
                {{ $t("common.save") }}
              </sweet-button>
            </b-card>
          </div>
        </right-panel>

        <div style="clear:both"></div>
        <right-panel class="float-right" id="sidebar-workspace-edit" width="600px" @hidden="rightPanelClosed">
          <div>
            <Form :hide-library="true" />
          </div>
        </right-panel>

        <h4 class="card-title mb-4">{{ $t("workspace.plural") }}</h4>

        <div style="width: 150px; height: 60px;">
          <div class="search-box mr-2 mb-2 d-inline-block">
            <div class="position-relative">
              <input v-model="nameFilter" type="text" class="form-control" placeholder="Ara..." />
              <i class="bx bx-search-alt search-icon"></i>
            </div>
          </div>
        </div>

        <div class="table-responsive mb-0" v-if="pagination && pagination.docs.length > 0">
          <table class="table table-centered table-nowrap">
            <tbody>
              <empty v-if="!pagination || pagination.docs.length == 0">
                <h5 class="text-center">
                  {{ $t("common.not-found") }}
                </h5>
              </empty>

              <tr v-for="item in pagination.docs" :key="item._id" class="hider" :class="{ 'dashed-border': item.isDeleting }">
                <td style="width: 50px;">
                  <i
                    v-if="item.isDeleting"
                    class="mdi mdi-progress-close font-size-20"
                    v-b-tooltip.hover.left
                    :title="$t('common.is-deleting')"
                    :class="{
                      'text text-success': selectedWorkspace && selectedWorkspace._id == item._id,
                    }"
                  ></i>
                  <i
                    v-else
                    class="mdi mdi-hexagon-multiple-outline font-size-20"
                    :class="{
                      'text text-success': selectedWorkspace && selectedWorkspace._id == item._id,
                    }"
                  ></i>
                </td>
                <td>
                  <h5 v-if="item.isDeleting" class="font-size-14 m-0">
                    {{ item.name }}
                  </h5>
                  <h5 v-else class="font-size-14 m-0 ">
                    {{ item.name }}
                  </h5>
                </td>
                <td>
                  <span v-b-tooltip.hover.left :title="$t('common.owner')" class="arrow">
                    <letter-avatar :showName="true" :name="item.owner.name" :image="item.owner.avatar" variant="secondary" />
                  </span>
                </td>
                <td>
                  <i v-if="item.isDeleting"></i>
                  <i v-if="item.isDeleting"></i>
                  <span v-else>
                    <i class="mdi mdi-account-group hand mr-3 font-size-16" v-b-tooltip.hover.left :title="$t('workspace.members')" @click="editMembers(item)"></i>
                    <i class="bx bx-money text-success hand ml-2 font-size-15" v-b-tooltip.hover.right :title="$t('purchased-feature.edit')" @click="editFeature(item)"></i>
                  </span>
                </td>

                <td class="text-right">
                  <!-- <i v-if="item.isDeleting"></i>
                  <i
                    v-else
                    class="bx bx-copy text-primary hand hidden mr-3"
                    v-b-tooltip.hover.left
                    :title="$t('common.duplicate')"
                    v-b-modal="'workspace-duplicate-option-modal' + item._id"
                  ></i> -->
                  <i v-if="item.isDeleting"></i>
                  <i v-else class="bx bx-pencil text-primary hand hidden mr-3" v-b-tooltip.hover.left :title="$t('common.edit')" @click="edit(item)"></i>
                  <i v-if="item.isDeleting"></i>
                  <i
                    v-else
                    class="bx bx-trash text-danger hand hidden mr-3"
                    v-b-tooltip.hover.left
                    :title="$t('common.delete')"
                    v-b-modal="'workspace-delete-option-modal'"
                    @click="select(item)"
                  ></i>

                  <!-- <WorkspaceDuplicateOptionModal
                    :id="'workspace-duplicate-option-modal' + item._id"
                    :workspace="item"
                    vuexStore="managementWorkspace"
                    @onSuccess="() => reload()"
                  ></WorkspaceDuplicateOptionModal> -->
                </td>
              </tr>
            </tbody>
          </table>
          <WorkspaceDeleteOptionModal
            v-if="selectedEntity"
            :id="'workspace-delete-option-modal'"
            :workspace="selectedEntity"
            vuexStore="managementWorkspace"
            @onSuccess="() => reload()"
          />
        </div>

        <template v-if="pagination">
          <div class="d-flex justify-content-center my-3">
            <ul class="pagination pagination-rounded mb-0">
              <b-pagination v-model="request.options.page" :total-rows="pagination.totalDocs" :per-page="pagination.limit" @click.native.stop size="sm"></b-pagination>
            </ul>
          </div>
        </template>
      </div>
    </div>

    <content-loading v-if="isLoading" />
  </div>
</template>

<script>
import { requiredIf } from "vuelidate/lib/validators";
import Form from "./management-workspace-form";
import ManagementWorkspaceMembersView from "./management-workspace-members-view";
import WorkspaceDeleteOptionModal from "./workspace-delete-option-modal";
import WorkspaceDuplicateOptionModal from "./workspace-duplicate-option-modal";
import WorkspaceInviteMember from "./workspace-invite-member";
const state = "managementWorkspace";
export default {
  components: {
    WorkspaceDeleteOptionModal,
    WorkspaceDuplicateOptionModal,
    ManagementWorkspaceMembersView,
    Form,
    WorkspaceInviteMember,
  },
  data() {
    return {
      request: {
        options: { page: 1, limit: 10 },
        query: {},
      },
      nameFilter: undefined,
      purchasedFeatures: {
        hasOrganizationLimit: false,
        organizationLimit: 10000,
        accessableProducts: [],
      },
    };
  },

  watch: {
    request: {
      deep: true,
      immediate: true,
      handler() {
        this.debounceMixin(() => this.paginate());
      },
    },
    nameFilter(n) {
      this.request.query = {
        ...this.request.query,
        name: { value: n },
      };
    },
  },
  computed: {
    isLoading() {
      return this.$store.getters[`${state}/isLoading`];
    },
    selectedWorkspace() {
      return this.$store.getters[`switchWorkspace/selected`];
    },
    selectedEntity() {
      return this.$store.getters[`${state}/selectedEntity`];
    },
    editedEntity() {
      return this.$store.getters[`${state}/editedEntity`];
    },
    pagination() {
      return this.$store.getters[`${state}/pagination`];
    },
  },

  validations: {
    purchasedFeatures: {
      organizationLimit: {
        required: requiredIf(function(form) {
          return form.hasOrganizationLimit;
        }),
      },
    },
  },
  methods: {
    rightPanelClosed() {
      this.$store.commit(`${state}/editedEntity`, null);
      this.$store.commit(`${state}/selectedEntity`, null);
      this.paginate();
    },
    reload() {
      setTimeout(() => {
        location.reload();
      }, 2000);
    },
    editFeature(item) {
      this.$store.commit(`${state}/editedEntity`, item);
      this.purchasedFeatures = { ...item.features };
      this.$root.$emit("bv::toggle::collapse", "sidebar-edit-workspace-purchased-feature");
    },
    async savePurchasedFeatures() {
      try {
        await this.$store.dispatch(`managementPurchasedWorkspaceFeature/update`, {
          payload: this.purchasedFeatures,
          sellable: "ORGANIZATION_LIMIT",
          workspaceId: this.editedEntity._id,
        });
        this.$toast.success(this.$t("common.successful"));
        this.purchasedFeatures = {};
        this.$root.$emit("bv::toggle::collapse", "sidebar-edit-workspace-purchased-feature");
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    edit(workspace) {
      this.$store.commit(`${state}/editedEntity`, { ...workspace });
      this.$root.$emit("bv::toggle::collapse", "sidebar-workspace-edit");
    },
    select(workspace) {
      this.$store.commit(`${state}/selectedEntity`, { ...workspace });
    },

    paginate() {
      this.$store.dispatch(`${state}/paginate`, this.request);
    },
    OnInvited() {
      this.$store.dispatch(`managementAccount/paginate`, this.request);
    },
    openCreateModal() {
      this.$root.$emit("bv::toggle::collapse", "sidebar-workspace-edit");
    },
    editMembers(item) {
      this.$store.commit(`${state}/selectedEntity`, { ...item });
      this.$root.$emit("bv::toggle::collapse", "sidebar-members-edit");
    },
    closeEditMember() {
      this.$root.$emit("bv::toggle::collapse", "sidebar-members-edit");
    },
  },
};
</script>
<style>
.hover-border-primary {
  border: 1px solid white;
}
.hover-border-primary:hover {
  border: 1px solid #556ee6 !important;
}
.dashed-border {
  border: 2px dashed rgb(87, 88, 93);
  background: rgb(244, 245, 245);
}
</style>
